import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface Popup {
  id: string;
  title: string;
  image_url: string;
  link_url: string | null;
  start_date: string;
  end_date: string;
}

const PopupModal: React.FC = () => {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hideToday, setHideToday] = useState(false);

  useEffect(() => {
    fetchActivePopups();
  }, []);

  const fetchActivePopups = async () => {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('popups')
      .select('*')
      .eq('is_active', true)
      .lte('start_date', now)
      .gte('end_date', now)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching popups:', error);
      return;
    }

    if (data && data.length > 0) {
      const hiddenPopups = getHiddenPopups();
      const visiblePopups = data.filter(popup => !hiddenPopups.includes(popup.id));

      if (visiblePopups.length > 0) {
        setPopups(visiblePopups);
        setIsVisible(true);
      }
    }
  };

  const getHiddenPopups = (): string[] => {
    const today = new Date().toDateString();
    const stored = localStorage.getItem('hiddenPopups');

    if (!stored) return [];

    try {
      const parsed = JSON.parse(stored);
      if (parsed.date === today) {
        return parsed.ids || [];
      }
      return [];
    } catch {
      return [];
    }
  };

  const hidePopupToday = (popupId: string) => {
    const today = new Date().toDateString();
    const hiddenPopups = getHiddenPopups();

    if (!hiddenPopups.includes(popupId)) {
      hiddenPopups.push(popupId);
    }

    localStorage.setItem('hiddenPopups', JSON.stringify({
      date: today,
      ids: hiddenPopups
    }));
  };

  const handleClose = () => {
    if (hideToday) {
      // 오늘 하루 안 보기: 모든 팝업을 숨김 처리
      popups.forEach(popup => hidePopupToday(popup.id));
    }
    setIsVisible(false);
  };

  const handlePrev = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : popups.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev < popups.length - 1 ? prev + 1 : 0));
  };

  const handleImageClick = () => {
    const currentPopup = popups[currentIndex];
    if (currentPopup?.link_url) {
      if (currentPopup.link_url.startsWith('http')) {
        window.open(currentPopup.link_url, '_blank');
      } else {
        window.location.href = currentPopup.link_url;
      }
    }
  };

  if (!isVisible || popups.length === 0) {
    return null;
  }

  const currentPopup = popups[currentIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* 닫기 버튼 */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-1 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors"
          aria-label="닫기"
        >
          <XMarkIcon className="w-6 h-6 text-gray-700" />
        </button>

        {/* 팝업 이미지 + 좌우 화살표 */}
        <div className="relative">
          <div
            className={`${currentPopup.link_url ? 'cursor-pointer' : ''}`}
            onClick={currentPopup.link_url ? handleImageClick : undefined}
          >
            <img
              src={currentPopup.image_url}
              alt={currentPopup.title}
              className="w-full h-auto"
            />
          </div>

          {/* 좌우 화살표 (팝업 2개 이상일 때만) */}
          {popups.length > 1 && (
            <>
              <button
                onClick={handlePrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/30 hover:bg-black/50 rounded-full transition-colors"
                aria-label="이전"
              >
                <ChevronLeftIcon className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/30 hover:bg-black/50 rounded-full transition-colors"
                aria-label="다음"
              >
                <ChevronRightIcon className="w-5 h-5 text-white" />
              </button>
            </>
          )}
        </div>

        {/* 하단 컨트롤 */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={hideToday}
              onChange={(e) => setHideToday(e.target.checked)}
              className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
            />
            <span className="ml-2 text-sm text-gray-600">오늘 하루 안 보기</span>
          </label>

          <div className="flex items-center gap-3">
            {popups.length > 1 && (
              <span className="text-sm text-gray-500">
                {currentIndex + 1} / {popups.length}
              </span>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupModal;
